import { ActionButton } from '@react-spectrum/s2';
import { useEffect, useState } from 'react';
import { Button as RACButton } from 'react-aria-components';

import { useBridgeApi } from '@/api/BridgeApi';
import { useOpenChatPanel } from '@/hooks/useOpenChatPanel';
import { PROCESS_STATUS_CONFIG } from '@/spaces/campaigns/processConstants';
import { useActiveJobs } from '@/spaces/chat/useActiveJobs';
import { cn } from '@/utils/cn';
import { formatRelativeTime } from '@/utils/formatRelativeTime';
import { trackEvent } from '@/utils/fullstory';

import type {
	Process,
	ProcessStatus,
} from '../../../../bridge/types/process.ts';
import { CAMPAIGN_ID_PREFIX } from '../../../../bridge/utils/processClassification.ts';

const STATUS_PILL_CLASSES: Record<ProcessStatus, string> = {
	active: 'bg-green-100 text-green-700',
	paused: 'bg-gray-100 text-gray-600',
	proposed: 'bg-amber-100 text-amber-700',
	cancelled: 'bg-rose-100 text-rose-700',
};

// A process can reach the UI with no title at all — `title: str = ""` is the
// default in shared/models/processes.py — so a row with neither a summary nor a
// title rendered blank: no accessible name to click, and the chat request read
// "Show me the  job". The terminal fallback matches resolveProcessTitle and
// ActiveCampaignPanel, which never let a title resolve to empty.
//
// A row carrying `job_artifact` stands for a job inside a container, so it is
// named by the job. `name` comes first because a monitor's `user_summary` is a
// whole sentence — summary_sentence() in artifact_content.py builds roughly 90
// characters — and the label below truncates. The sentence still shows in full
// in the artifact panel the row opens. The order matches JobRow in
// ActiveCampaignPanel.tsx, which labels the same monitors one level down.
const jobTitle = (job: Process): string =>
	job.job_artifact?.name ||
	job.job_artifact?.user_summary ||
	job.user_summary ||
	job.title ||
	'Job';

// Both the pill and the relative time belong to whatever the row stands for.
// Pause writes the artifact, not its container, so reading the container would
// paint a paused monitor as active and would age it by the campaign's clock.
const jobStatus = (job: Process): ProcessStatus =>
	job.job_artifact?.status ?? job.status;

const jobUpdatedAt = (job: Process): string =>
	job.job_artifact?.updated_at ?? job.updated_at;

// A campaign is not a job, so a row that reached the list through a job artifact
// inside a campaign must open the JOB. Only a `campaign_job:` container gets
// this treatment. A standalone monitor process keeps the process-panel click,
// because its own artifact panel carries no pause control while
// MonitorProcessPanel does — routing it to the artifact would take away the off
// switch ADO-1737 shipped.
const opensItsArtifact = (job: Process): boolean =>
	Boolean(job.job_artifact) && job.id.startsWith(CAMPAIGN_ID_PREFIX);

const JobRow = ({
	job,
	isLast,
	onPress,
	isDisabled,
}: {
	job: Process;
	isLast: boolean;
	onPress: () => void;
	isDisabled: boolean;
}) => (
	<RACButton
		onPress={onPress}
		isDisabled={isDisabled}
		className={cn(
			'-mx-2 flex flex-col gap-1 rounded-lg px-2 py-3 text-left outline-none',
			'cursor-pointer hover:bg-black/[0.02]',
			'disabled:cursor-not-allowed disabled:opacity-50',
			!isLast && 'border-b border-(--glass-border)',
		)}
	>
		<div className="flex w-full items-start justify-between gap-2">
			<span
				className="block min-w-0 flex-1 truncate text-sm font-semibold
					text-(--text-primary)"
			>
				{jobTitle(job)}
			</span>
			<span
				className={cn(
					'shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium',
					STATUS_PILL_CLASSES[jobStatus(job)],
				)}
			>
				{PROCESS_STATUS_CONFIG[jobStatus(job)].label}
			</span>
		</div>
		<span className="text-xs text-(--text-muted)">
			Updated {formatRelativeTime(jobUpdatedAt(job))}
		</span>
	</RACButton>
);

// Placeholder row shown during the first fetch. Matches JobRow's geometry (one
// title bar, one status pill, one subline) so the swap to real rows doesn't
// shift layout. research-shimmer carries the sweep animation and is already
// disabled under prefers-reduced-motion in index.css.
const JobRowSkeleton = ({ isLast }: { isLast: boolean }) => (
	<div
		className={cn(
			'-mx-2 flex flex-col gap-1 rounded-lg px-2 py-3',
			!isLast && 'border-b border-(--glass-border)',
		)}
	>
		<div className="flex w-full items-start justify-between gap-2">
			<div
				className="research-shimmer h-4 w-3/5 rounded-md"
				aria-hidden="true"
			/>
			<div
				className="research-shimmer h-5 w-14 shrink-0 rounded-full"
				aria-hidden="true"
			/>
		</div>
		<div className="research-shimmer h-3 w-24 rounded-md" aria-hidden="true" />
	</div>
);

export const JobsWidget = ({ jobs: jobsProp }: { jobs?: Process[] } = {}) => {
	const bridgeApi = useBridgeApi();
	const openChatPanel = useOpenChatPanel();
	const {
		jobs: fetched,
		loading,
		error,
		hasLoadedOnce,
		refresh,
	} = useActiveJobs();
	const jobs = jobsProp ?? fetched;
	// A caller-supplied jobs prop is authoritative, so the fetch-driven loading,
	// error, and skeleton states must not show in that case.
	const showFetchStates = jobsProp === undefined;
	const [isPending, setIsPending] = useState(false);

	const currentChatId = bridgeApi.user_state?.current_chat_id ?? null;

	useEffect(() => {
		setIsPending(false);
		void refresh();
	}, [currentChatId, refresh]);

	const handleJobPress = (job: Process) => {
		if (isPending) return;
		trackEvent('Job Clicked', {
			process_id: job.id,
			process_status: job.status,
			job_artifact_id: job.job_artifact?.id,
		});

		// A job inside a campaign opens its own panel, exactly as the campaign
		// dashboard opens it (ActiveCampaignPanel.tsx). This path must NOT set
		// isPending: do_open_chat_panel leaves current_chat_id alone, so the
		// effect above never re-runs, setIsPending(false) never fires, and every
		// row in the list would stay disabled for the rest of the session.
		//
		// It does need a chat, though. do_open_chat_panel_reducer returns no
		// deltas when current_chat_id is null (bridge/actions/chat.ts:2027), and
		// this widget draws only on the empty screen (Chat.tsx:191), where the
		// first chat is still in flight. Unguarded, that click is dead: no
		// panel, no chat, and no disabled row. do_create_chat below needs no
		// chat and establishes one, so it carries that window.
		if (currentChatId && opensItsArtifact(job) && job.job_artifact) {
			openChatPanel({
				panel_type: 'artifact',
				artifact_type: job.job_artifact.type,
				item_id: job.job_artifact.id,
			});
			return;
		}

		setIsPending(true);
		bridgeApi.sendAction({
			type: 'do_create_chat',
			chat_type: 'artifact_review',
			initial_message: `Show me the ${jobTitle(job)} job`,
			linked_item: {
				type: 'process',
				id: job.id,
				metadata: {
					title: jobTitle(job),
					description: job.description || undefined,
					artifact_type: 'job',
				},
			},
		});
	};

	return (
		<div className="flex h-full w-full flex-col">
			<span
				className="block pb-2 text-[0.625rem] font-semibold tracking-widest
					text-(--text-disabled) uppercase"
			>
				Jobs
			</span>
			{showFetchStates && loading && !hasLoadedOnce && jobs.length === 0 ? (
				<div role="status" aria-busy="true" aria-label="Loading jobs">
					{[0, 1, 2].map((i) => (
						<JobRowSkeleton key={i} isLast={i === 2} />
					))}
				</div>
			) : showFetchStates && error && jobs.length === 0 ? (
				<div
					aria-live="polite"
					className="flex flex-col items-start gap-2 py-2.5"
				>
					<span className="text-xs text-(--text-disabled)">
						Couldn’t load jobs.
					</span>
					<ActionButton isQuiet onPress={() => void refresh()}>
						Retry
					</ActionButton>
				</div>
			) : jobs.length === 0 ? (
				<span className="py-2.5 text-xs text-(--text-disabled)">
					No jobs yet
				</span>
			) : (
				<div
					className="scrollbar-hidden flex flex-1 flex-col overflow-x-hidden
						overflow-y-auto"
				>
					{jobs.map((job, i) => (
						<JobRow
							key={`${job.id}:${job.job_artifact?.id ?? ''}`}
							job={job}
							isLast={i === jobs.length - 1}
							onPress={() => handleJobPress(job)}
							isDisabled={isPending}
						/>
					))}
				</div>
			)}
		</div>
	);
};
